// src/components/instructor/ops/AssignControl.js
//
// Pass 49B §16/§18 — deferred assignment control. Candidates are CAPABILITY-gated via the
// canonical eligibleForActivity(): a lesson offers only participation=current teaching-capable
// staff; driving offers driving-capable; concierge offers concierge-capable. Category
// (School/Collective) is shown for context but is NOT the eligibility test, and unclassified
// is never silently treated as School. Disabled in read-only (historical) mode.

import React, { useMemo, useState } from 'react';
import { db } from '../../../firebase';
import { useAuth } from '../../../AuthContext';
import { assignInstructorToBooking } from '../../../utils/booking';
import { eligibleForActivity, deriveActivityType, ACTIVITY_LABEL, ACTIVITY } from '../../../utils/activityModel';
import { filterAreaAssignmentCandidates } from '../../../utils/operatingAreas';
import { filterDisciplineCandidates, bookingTeachingDiscipline } from '../../../utils/teachingDiscipline';
import { resolveInstructorCategory } from '../../../utils/instructorCategory';
import { categoryLabelForMember } from '../../../utils/organizationRuntime';

export default function AssignControl({ booking, seasonMembers = [], seasonId, organization = null, readOnly = false, onAssigned }) {
  const { currentUser, userData } = useAuth() || {};
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const activity = deriveActivityType(booking);

  // §16 canonical eligibility — capability, not role/category/historical team state.
  // Pass 83A §43/§45 — offer only staff eligible for the activity AND serving this booking's operating area, and NEVER
  // a TEST account as real production supply. A booking with no explicit area (transition) is area-unrestricted.
  // Pass 83B §24–§26 — for a client TEACHING lesson with an explicit discipline, also gate by the season-scoped
  // teaching discipline (ski→ski instructors, snowboard→snowboard). No discipline set → no gate; internal work
  // (driving/admin/concierge) ignores discipline entirely. Transition-safe: members with no discipline set stay eligible.
  const candidates = useMemo(() => {
    const areaEligible = filterAreaAssignmentCandidates(
      eligibleForActivity(seasonMembers, activity, seasonId),
      { operatingAreaId: booking?.operatingAreaId || '', seasonId },
    );
    const disciplineNeed = activity === ACTIVITY.LESSON ? bookingTeachingDiscipline(booking) : '';
    return filterDisciplineCandidates(areaEligible, { discipline: disciplineNeed, seasonId });
  }, [seasonMembers, activity, seasonId, booking]);

  if (readOnly) {
    return <span style={{ fontSize: 11, color: '#9E9B95' }} data-testid="assign-readonly">Read-only</span>;
  }

  const doAssign = async (member) => {
    if (!member) return;
    setBusy(true); setError('');
    try {
      await assignInstructorToBooking(db, {
        bookingId: booking.id,
        instructorId: member.userId,
        instructorName: member.name || member.displayName || member.userId,
        // Pass 56 (item J): record the actor so assignment audit is never blank.
        assignedByUserId: currentUser?.uid || userData?.uid || '',
        assignedByName: userData?.name || currentUser?.displayName || '',
        // Pass 57C (item 11): normalized authorization (owner/admin/ops + org scope).
        userData,
      });
      onAssigned && onAssigned(booking.id, member.userId);
    } catch (e) {
      setError(e?.message || 'Assignment failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <select
        data-testid={`assign-select-${booking.id}`}
        aria-label={`Assign ${ACTIVITY_LABEL[activity] || activity}`}
        disabled={busy || candidates.length === 0}
        defaultValue=""
        onChange={(e) => {
          const m = candidates.find((c) => c.userId === e.target.value);
          doAssign(m);
        }}
        style={{ fontSize: 12, padding: '3px 6px', borderRadius: 6, border: '1px solid #C8C5BE', background: '#fff' }}
      >
        <option value="" disabled>
          {candidates.length ? `Assign ${ACTIVITY_LABEL[activity] || activity}…` : `No ${ACTIVITY_LABEL[activity] || activity}-capable staff`}
        </option>
        {candidates.map((m) => {
          // Pass 82C.1 — org-aware category label (SAS→'SAS School'/'Collective'; other orgs→neutral). Stored category unchanged.
          const cat = resolveInstructorCategory(m, { season: seasonId });
          return (
            <option key={m.userId} value={m.userId}>
              {(m.name || m.displayName || m.userId)}{cat && cat !== 'unclassified' ? ` — ${categoryLabelForMember(m, seasonId, organization)}` : ''}
            </option>
          );
        })}
      </select>
      {error ? <span style={{ fontSize: 10, color: '#DC2626' }}>{error}</span> : null}
    </span>
  );
}
